import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Check, Loader2, RefreshCcw, Save, X } from "lucide-react";
import { TrashBinIcon } from "../../assets/icons";

import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordian";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { cn } from "../../utils/lib";

import { CAR_TYPES } from "../../constants/cars";

// import {
//   incrementRefetchCountCity,
// } from "../../redux/pricing-new/pricing-slice";

import {
  addCarInZone,
  changeOneWayZoneForm,
  changeZoneStatus,
  removeCarInZone,
  setOnewayAccordianIndex,
} from "../../redux/pricing/slice";

export function TableOnewayPricing() {
  const dispatch = useDispatch();

  const accordionRef = React.useRef(null);
  const selectedAccordionRef = React.useRef(null);

  const pricingTableRef = React.useRef(null);
  const inputRefs = React.useRef({});

  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isZoneDeleting, setIsZoneDeleting] = React.useState(false);
  const [isZoneDeletingError, setIsZoneDeletingError] = React.useState(false);

  const token = useSelector(({ auth }) => auth.token);

  const currentSelectedCityId = useSelector(
    ({ pricing }) => pricing.currentSelectedCityId
  );

  const currentSelectedCity = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId]
  );

  const oneWayAccordionIndex = useSelector(
    ({ pricing }) => pricing.oneWayZoneActiveIndex
  );

  const zones = useSelector(({ pricing }) => pricing.zones);

  React.useEffect(() => {
    if (oneWayAccordionIndex && accordionRef.current) {
      setTimeout(() => {
        if (!selectedAccordionRef.current) return;

        selectedAccordionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 300);
    }
  }, [oneWayAccordionIndex]);

  const pricing = useSelector(({ pricing }) => pricing);

  const handleSaveZonePricing = async (zoneId) => {
    dispatch({
      type: "EXTRANET_UPDATE_ZONE_PRICNG_BY_ID",
      payload: {
        zoneId,
      },
    });
  };

  const { isFetching, isError, currency } = useSelector(
    ({ pricing }) => pricing.cities[pricing.currentSelectedCityId]
  );

  const onewayPricingKeys = Object.keys(zones).filter(
    (zoneId) => zones[zoneId].bookingType === "ONEWAY"
  );

  const currentCityPricingKeys = onewayPricingKeys.filter(
    (key) => currentSelectedCityId === zones[key].cityId
  );

  const onewayPricingKeysLength = currentCityPricingKeys.length;

  if (isFetching)
    return (
      <div className="py-16">
        <Loader2 className="mx-auto size-4 animate-spin text-white" />
      </div>
    );

  if (isError) {
    return (
      <div className="space-y-2 py-16">
        <RefreshCcw
          onClick={
            () => {}
            // dispatch(
            //   incrementRefetchCountCity({ cityId: currentSelectedCityId })
            // )
          }
          className="mx-auto text-red-500 hover:cursor-pointer"
        />
        <p className="mx-auto max-w-40  text-center text-sm font-medium">
          Error Occurred while fetching zones for {currentSelectedCity.cityName}
        </p>
      </div>
    );
  }

  return (
    <>
      {isFetching && <Loader2 className="mx-auto pt-24" />}

      {onewayPricingKeysLength === 0 && (
        <p className="text-sm text-gray-300">
          No Pricing set for oneway in {currentSelectedCity.cityName}
        </p>
      )}

      <Accordion
        className="space-y-2"
        type="single"
        collapsible
        value={oneWayAccordionIndex ?? undefined}
        onValueChange={(key) => dispatch(setOnewayAccordianIndex({ key }))}
        ref={accordionRef}
      >
        {currentCityPricingKeys.map((zoneId) => {
          const zone = zones[zoneId];

          const areAllCarsSelected = CAR_TYPES.every(
            (CAR) => !!zone.pricing?.[CAR.categoryType]
          );

          return (
            <AccordionItem
              ref={
                oneWayAccordionIndex === zoneId ? selectedAccordionRef : null
              }
              className="border border-[#353c4a] rounded-md p-2 2md:p-4"
              value={zoneId}
              key={zoneId}
            >
              <AccordionTrigger className="hover:no-underline pr-1 text-white grid grid-cols-[1fr_auto]">
                <p className="text-left truncate">
                  {zone.zoneName ?? "Zone name is not given"}
                </p>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 overflow-x-scroll pt-4 scrollbar-none">
                <div className="flex flex-wrap gap-4">
                  <div className="relative border border-[#353c4a] rounded-md min-w-80 h-80 flex-1 overflow-y-scroll scrollbar-none">
                    <div className="sticky border-b top-0 bg-gradient-to-l from-black via-zinc-800 to-black rounded-tl-lg rounded-tr-lg grid grid-cols-[2rem_1fr] items-center px-4 pl-2 py-3 border-[#353c4a]">
                      <div className="w-8 flex items-center justify-center">
                        <Checkbox
                          id={`select-all-${zoneId}`}
                          checked={areAllCarsSelected}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              CAR_TYPES.forEach((CAR) => {
                                dispatch(
                                  addCarInZone({
                                    zoneId,
                                    carName: CAR.categoryType,
                                    carPrice: { transferPrice: "0" },
                                  })
                                );
                              });
                            } else {
                              CAR_TYPES.forEach((CAR) => {
                                dispatch(
                                  removeCarInZone({
                                    zoneId,
                                    carName: CAR.categoryType,
                                  })
                                );
                              });
                            }

                            dispatch(
                              changeZoneStatus({
                                zoneId,
                                newStatus: {
                                  isChanged: true,
                                },
                              })
                            );
                          }}
                          className="mx-auto bg-[#181E29] border-[#4b5563]"
                        />
                      </div>
                      <Label
                        htmlFor={`select-all-${zoneId}`}
                        className="text-xs xl:text-base font-medium leading-none capitalize text-white"
                      >
                        Car Types
                      </Label>
                    </div>
                    {CAR_TYPES.map((CAR, index) => (
                      <div
                        className={cn(
                          " grid grid-cols-[2rem_1fr] items-start px-4 pl-2 py-2 text-white",
                          CAR_TYPES.length - 1 !== index &&
                            "border-b border-zinc-800"
                        )}
                      >
                        <Checkbox
                          id={CAR.id}
                          checked={!!zones[zoneId].pricing?.[CAR.categoryType]}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              dispatch(
                                addCarInZone({
                                  zoneId,
                                  carName: CAR.categoryType,
                                  carPrice: { transferPrice: "0" },
                                })
                              );
                              setTimeout(() => {
                                if (pricingTableRef.current) {
                                  pricingTableRef.current.scrollTo({
                                    top: 0,
                                    behavior: "smooth",
                                  });
                                }
                              }, 100);
                              setTimeout(() => {
                                if (
                                  !inputRefs.current[
                                    `${zoneId}-${CAR.categoryType}`
                                  ]
                                )
                                  return;

                                inputRefs.current[
                                  `${zoneId}-${CAR.categoryType}`
                                ]?.focus();
                              }, 300);
                            } else {
                              dispatch(
                                removeCarInZone({
                                  zoneId,
                                  carName: CAR.categoryType,
                                })
                              );
                            }

                            dispatch(
                              changeZoneStatus({
                                zoneId,
                                newStatus: {
                                  isChanged: true,
                                },
                              })
                            );
                          }}
                          className="mx-auto bg-[#181E29] border-[#4b5563]"
                        />
                        <Label
                          htmlFor={CAR.id}
                          className="hover:cursor-pointer"
                        >
                          <p className="text-base font-medium capitalize leading-none">
                            {CAR.categoryType}
                          </p>
                          <p className="text-xs font-light leading-4">
                            {CAR.description}
                          </p>
                        </Label>
                      </div>
                    ))}
                  </div>
                  <div
                    ref={pricingTableRef}
                    className="relative border border-[#353c4a] rounded-md min-w-80 h-80 flex-1 overflow-y-scroll scrollbar-none"
                  >
                    <div className="sticky border-b top-0 bg-gradient-to-l from-black via-zinc-800 text-white to-black rounded-tl-lg rounded-tr-lg grid grid-cols-[2fr_1fr] items-center px-4 pl-2 py-3 border-[#353c4a]">
                      <p className="text-xs font-medium capitalize leading-none">
                        Selected Car Types
                      </p>
                      <p className="mx-auto">Transfer Price ({currency})</p>
                    </div>
                    {Object.keys(zones[zoneId].pricing ?? {}).map(
                      (CAR, index) => {
                        return (
                          <div
                            className={cn(
                              "grid grid-cols-[2fr_1fr] items-center px-4 pl-2 py-2",
                              CAR_TYPES.length - 1 !== index &&
                                "border-b border-[#353c4a]"
                            )}
                          >
                            <div>
                              <p className="text-xs xl:text-base font-medium leading-none capitalize text-white">
                                {CAR}
                              </p>
                            </div>
                            <div className="mx-auto">
                              <Input
                                type="number"
                                className="bg-[#181E29] border border-[#353c4a] block w-full truncate ring-1 ring-transparent placeholder:truncate focus:outline-none focus-visible:border disabled:border disabled:opacity-90 disabled:bg-gray-100 disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-black text-center text-white focus:border-none"
                                min={0}
                                value={
                                  zones[zoneId].pricing?.[CAR].transferPrice ??
                                  0
                                }
                                onChange={(e) => {
                                  if (
                                    e.target.value < "0" &&
                                    e.target.value > "9"
                                  )
                                    return;
                                  dispatch(
                                    changeOneWayZoneForm({
                                      zoneId,
                                      carName: CAR,
                                      carPrice: {
                                        transferPrice: e.target.value,
                                      },
                                    })
                                  );

                                  dispatch(
                                    changeZoneStatus({
                                      zoneId,
                                      newStatus: {
                                        isChanged: true,
                                      },
                                    })
                                  );
                                }}
                                ref={(el) => {
                                  inputRefs.current[`${zoneId}-${CAR}`] = el;
                                }}
                              />
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap justify-between gap-2">
                  <AlertDialog open={isDialogOpen}>
                    <AlertDialogTrigger
                      onClick={() => setIsDialogOpen(true)}
                      asChild
                    >
                      <TrashBinIcon className="rounded-md size-10 p-2 hover:cursor-pointer hover:bg-white/20 bg-white/10" />
                    </AlertDialogTrigger>
                    <AlertDialogContent className="w-[calc(100%_-_2.5rem)] rounded-md">
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Are you absolutely sure?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          <p className="mb-2 ">
                            This action cannot be undone. This will permanently
                            delete your pricing for{" "}
                            <em className="inline-block font-medium not-italic text-black">
                              {zone.zoneName}
                            </em>
                            and remove your data from our servers.
                          </p>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel
                          disabled={isZoneDeleting}
                          onClick={() => setIsDialogOpen(false)}
                        >
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          disabled={isZoneDeleting}
                          className="border border-red-500 bg-red-50 text-red-500 hover:bg-red-100 disabled:border-red-200"
                          onClick={() =>
                            dispatch({
                              type: "EXTRANET_REMOVE_ZONE_BY_ID",
                              payload: {
                                token,
                                zoneId,
                                callbacks: {
                                  setIsZoneDeleting,
                                  setIsZoneDeletingError,
                                  setIsDialogOpen,
                                },
                              },
                            })
                          }
                        >
                          {isZoneDeletingError && <X />}
                          {isZoneDeleting && (
                            <Loader2 className="animate-spin" />
                          )}
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  {/* Redux Logic For Saving with server */}
                  {!pricing.zoneStatus[zoneId].isChanged && (
                    <Button
                      variant={"outline"}
                      type="button"
                      className="bg-transparent hover:bg-transparent border-[#353c4a]"
                    >
                      <Check className="text-green-500" />
                      <span className="text-white ">Up to date</span>
                    </Button>
                  )}

                  {pricing.zoneStatus[zoneId].isChanged && (
                    <Button
                      type="button"
                      onClick={() => {
                        handleSaveZonePricing(zoneId);
                      }}
                    >
                      {pricing.zoneStatus[zoneId].isLoading && (
                        <Loader2 className="animate-spin" />
                      )}
                      {pricing.zoneStatus[zoneId].isError && (
                        <RefreshCcw className="text-red-500" />
                      )}
                      {!pricing.zoneStatus[zoneId].isLoading &&
                        !pricing.zoneStatus[zoneId].isError && <Save />}
                      <span className="hidden sm:block">Save Pricing</span>
                    </Button>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </>
  );
}
